import axios from "axios";
import queryString from "query-string";
import { Auth0Client } from "@auth0/auth0-spa-js";
import * as CryptoJS from "crypto-js";
import * as DateUtilities from "../utilities/dateUtilities";
import { constants } from "../constants";
import * as PersistItemsAction from "@mywvglobal-shared/mywv-shared/action/persistItemsAction";
import jwt_decode from "jwt-decode";
import { logoutUser } from "./LogOutUtils";
import * as LogoutAction from "@mywvglobal-shared/mywv-shared/action/persistItemsAction";

/**
 * TokenManager is responsible for handling authentication tokens, including retrieval,
 * decryption, validation, and refreshing of tokens for different authentication providers
 * (e.g., OneLogin, Auth0). It manages token storage in localStorage, interacts with
 * reducers for state persistence, and ensures that tokens are refreshed before expiration.
 */
class TokenManager {
  constructor(loginModule, secretKey, dispatch, persistItemsReducer, loginReducer, navigate) {
    this.loginModule = loginModule;
    this.dispatch = dispatch;
    this.secretKey = secretKey;
    this.persistItemsReducer = persistItemsReducer;
    this.loginReducer = loginReducer;
    this.navigate = navigate;
    this.tokenRefreshPromise = null;
  }

  /**
   * Retrieves and decrypts the token response from localStorage.
   *
   * Decrypts the value stored under the "tokenResp" key using AES and the instance's secretKey.
   * Returns the parsed JSON object if available and valid, otherwise returns null.
   *
   * @returns {Object|null} The decrypted token response object, or null if not found or invalid.
   */
  getTokenResp() {
    if (localStorage.getItem("tokenResp") && localStorage.getItem("tokenResp") !== "") {
      const bytes = CryptoJS.AES.decrypt(localStorage.getItem("tokenResp"), this.secretKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    }
    return null;
  }

  /**
   * Retrieves the ID token from the token response.
   *
   * @returns {string|undefined} The ID token if available, otherwise undefined.
   */
  getIdToken() {
    return this.getTokenResp()?.id_token;
  }

  /**
   * Retrieves the refresh token from the token response.
   *
   * @returns {string|undefined} The refresh token if available, otherwise undefined.
   */
  getRefreshToken() {
    return this.getTokenResp()?.refresh_token;
  }


  /**
   * Determines whether the current access token is expiring soon and may need to be refreshed.
   *
   * Calculates the time difference between the current time and the token's expiration time.
   * Compares this difference to the configured refresh interval to decide if the token is expiring.
   * For the "OneLogin" provider, also checks if a refresh token is available.
   *
   * @returns {boolean} Returns true if the token is expiring soon (within the refresh interval), otherwise false.
   */
  isTokenExpiring() {
    const timeDiff = (DateUtilities.getMinutesDifferenceFromUnix(this.persistItemsReducer.expirationTime) * -1);
    //const accessTokenRenewIntervalInMinutes = 300;
     const accessTokenRenewIntervalInMinutes = this.loginModule?.refreshTokenIntervalInMinutes
       ? (this.loginModule?.refreshTokenIntervalInMinutes) : 0;

    if (this.loginModule.provider === "OneLogin") {
      return (timeDiff < accessTokenRenewIntervalInMinutes) && !!this.getRefreshToken();
    } else {
      return (timeDiff < accessTokenRenewIntervalInMinutes);
    }
  }

  /**
   * Refreshes the access token if it is close to expiring.
   * If the token is not expiring, returns the current access token.
   * If a token refresh is already in progress, returns the existing refresh promise to prevent race conditions.
   * Otherwise, initiates a new token refresh and returns the resulting promise.
   *
   * @returns {Promise<string>} A promise that resolves to a valid access token.
   */
  async refreshTokenIfNeeded() {
    // If the token is not expiring, return the current one.
    if (!this.isTokenExpiring()) {
      return this.persistItemsReducer.accessToken;
    }

    // If a refresh is already in progress, return the existing promise to avoid race conditions.
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    // Start the refresh and store the promise.
    this.tokenRefreshPromise = this.performTokenRefresh().finally(() => {
      // After the promise settles (for all callers), clear it
      // so that subsequent calls can trigger a new refresh if needed.
      this.tokenRefreshPromise = null;
    });

    return this.tokenRefreshPromise;
  }

  /**
   * Refreshes the authentication token based on the configured login provider.
   *
   * For "OneLogin", it sends a POST request to the token endpoint using the refresh token.
   * For "Auth0", it uses the Auth0Client to silently obtain a new access token.
   * Handles the token response and returns the new access token as a string.
   * In case of failure, logs the error and returns an empty string.
   * Ensures the token refresh promise is cleared after each attempt.
   *
   * @async
   * @returns {Promise<string>} The refreshed access token, or an empty string if the refresh fails.
   */
  async performTokenRefresh() {
    try {

      let tokenData = null;

      if (this.loginModule.provider === "OneLogin") {
        const token_uri = this.loginModule.domainName.includes("https://")
          ? this.loginModule.domainName + "/token"
          : "https://" + this.loginModule.domainName + "/token";
        const req_params = {
          client_id: this.loginModule.clientId,
          grant_type: "refresh_token",
          refresh_token: this.getRefreshToken(),
        };
        const form = queryString.stringify(req_params);
        tokenData = await axios.post(token_uri, form, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          crossDomain: true,
        });
        this.handleTokenResponse(tokenData);
        return tokenData?.data?.access_token || "";
      }

      if (this.loginModule.provider === "Auth0") {
        const auth0 = new Auth0Client({
          domain: this.loginModule.domainName,
          clientId: this.loginModule.clientId,
          authorizationParams: {
            redirect_uri: constants?.currentOrigin,
            audience: "https://api.experienceportal.worldvision.org.nz/",
            scope: "openid params profile email offline_access",
          },
        });
        tokenData = await auth0.getTokenSilently({ detailedResponse: true });
        this.handleTokenResponse({ data: tokenData });
        return tokenData?.access_token || "";
      }

      return "";
    } catch (error) {
      // Log the error for debugging purposes and clear the promise to allow for a retry.
      console.error("Token refresh failed:", error);
      logoutUser({
        persistItemsReducer: this.persistItemsReducer,
        loginReducer: this.loginReducer,
        dispatch: this.dispatch,
        LogoutAction,
        constants,
        navigate: this.navigate,
      });
      return "";
    }
  }

  /**
   * Handles the token response by encrypting and storing it in localStorage,
   * and optionally dispatching a login persist action to update the persistent reducer.
   *
   * @param {Object} tokenData - The token response object, expected to have a `data` property containing token details.
   * @param {Object} tokenData.data - The actual token data, including access and refresh tokens.
   * @returns {void}
   */
  handleTokenResponse(tokenData) {
    if (tokenData?.data) {
      // Save to localStorage
      const cipherText = CryptoJS.AES.encrypt(JSON.stringify(tokenData.data), this.secretKey).toString();
      localStorage.setItem("tokenResp", cipherText);

      // Save to persistent reducer if dispatch and action are provided
      if (this.dispatch) {
        // Prepare param object for reducer (customize as needed)
        const response = tokenData.data;
        const expirationTime = parseInt(
          (DateUtilities.getCurrentTimeInMilliSec() + ((parseInt(response.expires_in / 60)) * 60000)) / 1000
        );

        // Decode token to get email, ensuring it's always present after a refresh.
        const decoded = response.id_token ? jwt_decode(response.id_token) : {};
        const emailValues = Object.keys(decoded).filter((propertyName) => (
          propertyName.includes("email") &&
          !propertyName.includes("email_verified") &&
          !propertyName.includes("emailverified")
        ));
        let email = "";
        if (emailValues.length > 0) {
          email = decoded[emailValues[0]];
        }

        const param = {
          ...this.persistItemsReducer,
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          expirationTime,
          email: email || this.persistItemsReducer.emailID, // Use 'email' to match the reducer's expectation
          saveAuthParams: { ...this.persistItemsReducer?.saveAuthParams, id_token: response.id_token }
        };
        this.dispatch(PersistItemsAction.loginPersist(param));
      }
    }
  }

  async getAuthHeaders() {
    const token = await this.refreshTokenIfNeeded();
    return {
      Authorization: `Bearer ${token}`,
      SOCode: this.persistItemsReducer?.selectedSOData?.soCode,
    };
  }

}

export default TokenManager;